import {
  FieldSet,
  FormattedString,
  InputWizardPanelComponent,
  RadioGroup,
  RadioGroupOption,
  Select,
  TextInput,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { LicenseTypeSelect } from "../../components/LicenseTypeSelect/LicenseTypeSelect";
import { StorageSizeSelect } from "../../components/StorageSizeSelect/StorageSizeSelect";
import { VmDbShapeConfigInput, VmShapeConfigValue } from "../../components/VmDbShapeConfigInput/VmDbShapeConfigInput";
import { VMDbVersionSelect } from "../../components/VmDbVersionSelect/VMDbVersionSelect";
import { vmdbPluggableDatabaseUrl } from "../../constants/docConstants";
import {
  VmdbDbSystemDatabaseEditionEnum,
  VmdbDbSystemOptionsStorageManagementEnum,
} from "../../gen/clients/mchub-azure-api-client-vmdb";
import {
  CharacterSets,
  defaultCharacterSet,
  defaultNationalCharacterSet,
  getSoftwareEditionDisplayName,
  NationalCharacterSets,
  SOFTWARE_EDITION_TYPE,
  STORAGE_TYPE,
  VmdbCreateFlowType,
  vmSoftwareEdition,
} from "../../helpers/resourceHelper";
import {
  alphaNumericRegex,
  alphaNumericUnderscoreRegex,
  pdbNameMaxLength,
  startWithLetterRegex,
} from "../../helpers/validationHelper";

export const GROUP_CONFIG = "config";

export enum Fields {
  StorageType = "vmdbDbSystemDeploymentDetails.dbSystemOptions",
  StorageSize = "vmdbDbSystemDeploymentDetails.initialDataStorageSizeInGBs",
  SystemModel = "vmdbDbSystemDeploymentDetails.shape",
  SoftwareEdition = "vmdbDbSystemDeploymentDetails.databaseEdition",
  LicenseType = "vmdbDbSystemDeploymentDetails.licenseModel",
  DatabaseVersion = "vmdbDbSystemDeploymentDetails.dbHome.dbVersion",
  DatabaseName = "vmdbDbSystemDeploymentDetails.dbHome.database.dbName",
  PdbName = "vmdbDbSystemDeploymentDetails.dbHome.database.pdbName",
  NodeCount= "vmdbDbSystemDeploymentDetails.nodeCount",
  BackupName = "vmdbDbSystemDeploymentDetails.dbHome.database.backupId",
  CharacterSet = "mdbDbSystemDeploymentDetails.dbHome.database.characterSet",
  NationalCharacterSet = "mdbDbSystemDeploymentDetails.dbHome.database.ncharacterSet",
}
export interface BackupInfo {
  id: string,
  name: string,
  startDate: Date | undefined,
}

export interface ConfigTabContentProps {
  createFlowType?: VmdbCreateFlowType;
  inputWizardPanelRef: InputWizardPanelComponent;
  subscriptionId: string;
  location: string;
  backup?: BackupInfo;
  disableUIControls?: Fields[] | undefined;
}

const defaultNodeCount = "1";
const defaultStorageSize = "256";

enum NODE_COUNT {
  ONE = "1",
  TWO = "2",
}

const characterSetOptions = CharacterSets.map(set => ({
  id: set,
  text: set,
}));

const natioanlCharacterSetOptions = NationalCharacterSets.map(set => ({
  id: set,
  text: set,
}));

const nodeCountOptions = (): RadioGroupOption[] => ([
  {
    id: NODE_COUNT.ONE,
    text: NODE_COUNT.ONE,
  },
  {
    id: NODE_COUNT.TWO,
    text: NODE_COUNT.TWO,
  },
] as unknown as RadioGroupOption[]);

const storageTypeOptions = (): RadioGroupOption[] => ([
  {
    id: STORAGE_TYPE.ASM,
    text: Messages.labels.storageTypeASM(),
  },
  {
    id: STORAGE_TYPE.LVM,
    text: Messages.labels.storageTypeLVM(),
  },
] as RadioGroupOption[]);

export const ConfigTabContent = (
  { createFlowType, inputWizardPanelRef, subscriptionId, location, backup, disableUIControls }: ConfigTabContentProps,
): JSX.Element => {
  const [configValue, setConfigValue] = React.useState<VmShapeConfigValue | undefined>();
  const [isPdbSupported, setIsPdbSupported] = React.useState<boolean | undefined>(true);
  const [storageType, setStorageType] = React.useState<VmdbDbSystemOptionsStorageManagementEnum>(STORAGE_TYPE.ASM);
  const [nodeCount, setNodeCount] = React.useState(defaultNodeCount);
  const [softwareEdition, setSoftwareEdition] = React.useState<VmdbDbSystemDatabaseEditionEnum>(
    SOFTWARE_EDITION_TYPE.ENTERPRISE_EDITION_HIGH_PERFORMANCE,
  );
  const [nodeKey, setNodeKey] = React.useState(uniqueGUID());

  const onError = (): void => inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.general());
  const onMissingDependencies = (): void => inputWizardPanelRef.showErrorDialog(
    Messages.createCommon.dependencyWarnings.basicsIncomplete.message(),
    Messages.createCommon.dependencyWarnings.basicsIncomplete.title(),
  );
  const softwareEditionOptions = vmSoftwareEdition.map(edition => ({
    id: edition.id,
    text: getSoftwareEditionDisplayName(edition.id),
  }));

  // eslint-disable-next-line max-len
  const shouldRestrictNodeCount = (minCore: number, maxCore: number): boolean => minCore === 1 && maxCore === 1;

  const onShapeChange = (value: VmShapeConfigValue): void => {
    const shapeModel = value.shape;
    setConfigValue({
      shape: value.shape,
      cpuCoreCount: value.cpuCoreCount,
    });

    if (shouldRestrictNodeCount(shapeModel.minimumCoreCount || 0, shapeModel.availableCoreCount)) {
      setNodeCount(NODE_COUNT.ONE);
      setNodeKey(uniqueGUID());
    }
  };

  const onNodeCountChange = (nodeCountId: string): void => {
    setNodeCount(nodeCountId);

    if (nodeCountId === NODE_COUNT.TWO) {
      setSoftwareEdition(SOFTWARE_EDITION_TYPE.ENTERPRISE_EDITION_EXTREME_PERFORMANCE);
    }
  };

  return (
    <>
      <FieldSet
        header={Messages.createVmDb.configurationTab.sectionTitles.databaseSystemDetails()}
      >
        <VmDbShapeConfigInput
          required
          subscriptionId={subscriptionId}
          location={location}
          fieldName={Fields.SystemModel}
          label={Messages.labels.shapeSelection()}
          tooltip={Messages.hints.selectVMShape()}
          onChange={onShapeChange}
          onError={onError}
          onMissingDependencies={onMissingDependencies}
        />
        <RadioGroup
          required
          key={nodeKey}
          fieldName={Fields.NodeCount}
          label={Messages.labels.totalNodeCount()}
          options={nodeCountOptions()}
          // eslint-disable-next-line max-len
          disabled={shouldRestrictNodeCount(configValue?.shape.minimumCoreCount || 0, configValue?.shape.availableCoreCount || 0)}
          defaultValue={nodeCount}
          onChange={onNodeCountChange}
        />
        {false && (
          <RadioGroup
            required
            fieldName={Fields.StorageType}
            label={Messages.labels.storageType()}
            options={storageTypeOptions()}
            defaultValue={storageType}
            onChange={
              (sType: string) => {
                setStorageType(sType as VmdbDbSystemOptionsStorageManagementEnum);
              }
            }
          />
        )}
        <StorageSizeSelect
          required
          storageType={storageType}
          defaultValue={[defaultStorageSize]}
          fieldName={Fields.StorageSize}
          label={Messages.labels.availableDataStorageGB()}
        />
        <Select
          required
          key={softwareEdition}
          fieldName={Fields.SoftwareEdition}
          label={Messages.labels.softwareEdition()}
          disabled={nodeCount === NODE_COUNT.TWO}
          defaultValue={softwareEdition ? [softwareEdition] : undefined}
          options={softwareEditionOptions}
          onChange={
            (edition: string) => {
              setSoftwareEdition(edition as VmdbDbSystemDatabaseEditionEnum);
            }
          }
          hideSearchBox
        />
        <LicenseTypeSelect fieldName={Fields.LicenseType} />
      </FieldSet>
      <FieldSet
        header={Messages.createVmDb.configurationTab.sectionTitles.databaseDetails()}
      >
        {createFlowType !== VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP && (
          <VMDbVersionSelect
            required
            shape={configValue?.shape.name}
            subscriptionId={subscriptionId}
            location={location}
            fieldName={Fields.DatabaseVersion}
            label={Messages.labels.databaseVersion()}
            tooltip={Messages.labels.databaseVersion()}
            onError={onError}
            onMissingDependencies={onMissingDependencies}
            isPdbSupported={(supportsPdb: boolean | undefined) => setIsPdbSupported(supportsPdb || undefined)}
          />
        )}
        {createFlowType === VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP && (
          <TextInput
            fieldName={Fields.BackupName}
            label={Messages.labels.databaseBackupName()}
            disabled={disableUIControls?.find(ele => ele === Fields.BackupName) !== undefined}
            defaultValue={`${backup?.name} (${backup?.startDate})`}
          />
        )}
        <TextInput
          required
          fieldName={Fields.DatabaseName}
          label={Messages.labels.databaseName()}
          tooltip={Messages.labels.databaseName()}
          validator={(value: string) => {
            const errors: string[] = [];
            if (value && !startWithLetterRegex.test(value)) {
              errors.push(Messages.validation.nameStartChar());
            }
            if (value && !alphaNumericRegex.test(value)) {
              errors.push(Messages.validation.valueAlphaNumeric());
            }
            if (value?.length > 8) {
              errors.push(Messages.validation.valueMaxLen("8"));
            }
            return errors.length > 0 ? errors : undefined;
          }}
        />
        {createFlowType !== VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP && isPdbSupported && (
          <TextInput
            fieldName={Fields.PdbName}
            label={Messages.labels.pdbName()}
            tooltip={
              FormattedString(
                { inputText: Messages.hints.tooltipPluggableDatabase(vmdbPluggableDatabaseUrl) },
              ) as unknown as string
            }
            validator={(value: string) => {
              const errors: string [] = [];
              if (value && !startWithLetterRegex.test(value)) {
                errors.push(Messages.validation.nameStartChar());
              }
              if (value?.length > pdbNameMaxLength) {
                errors.push(Messages.validation.valueMaxLen(pdbNameMaxLength.toString()));
              }
              if (value && !alphaNumericUnderscoreRegex.test(value)) {
                errors.push(Messages.validation.valueAlphaNumericUnderscore());
              }
              return errors.length > 0 ? errors : undefined;
            }}
          />

        )}
        <Select
          label={Messages.labels.characterSet()}
          required
          fieldName={Fields.CharacterSet}
          options={characterSetOptions}
          defaultValue={[defaultCharacterSet]}
        />
        <Select
          label={Messages.labels.nationalCharacterSet()}
          required
          fieldName={Fields.NationalCharacterSet}
          options={natioanlCharacterSetOptions}
          defaultValue={[defaultNationalCharacterSet]}
        />
      </FieldSet>
    </>
  );
};
